import { Link } from "react-router-dom";
import { Flower, EnvelopeSimple, ArrowLeft, HandHeart, ShieldCheck, Sparkle, Copy, Check } from "@phosphor-icons/react";
import { useState } from "react";
import SiteFooter from "@/components/SiteFooter";
import usePageMeta from "@/lib/usePageMeta";

const EMAIL = "hi@mywingwomen.com";

const REASONS = [
  { icon: HandHeart, title: "Say hi", body: "Share your journey, feedback, or a story of what worked. We read every message." },
  { icon: ShieldCheck, title: "Privacy & data", body: "Requests to access, correct, or delete your Wingwomen data. We respond within 30 days." },
  { icon: Sparkle, title: "Partnerships", body: "Clinicians, hospitals, and patient-advocacy groups, we'd love to talk about how we can help together." },
];

export default function Contact() {
  usePageMeta({
    title: "Contact",
    description: "Reach the Wingwomen team at hi@mywingwomen.com, for support, privacy requests, or partnerships.",
  });
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try { await navigator.clipboard.writeText(EMAIL); setCopied(true); setTimeout(() => setCopied(false), 2000); }
    catch {}
  };

  return (
    <div className="min-h-screen bg-cream text-ink">
      <header className="glass sticky top-0 z-40">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
          <Link to="/" data-testid="contact-brand-link" className="flex items-center gap-2">
            <Flower size={22} weight="duotone" className="text-coral-700" />
            <span className="font-display text-xl">Wingwomen</span>
          </Link>
          <Link to="/" className="text-sm hover:text-coral-700 flex items-center gap-1.5">
            <ArrowLeft size={14} /> Back
          </Link>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-16">
        <div className="text-xs tracking-[0.25em] uppercase text-sage-600 mb-4"><span className="rule" />We're listening</div>
        <h1 className="font-display text-5xl sm:text-6xl leading-tight" data-testid="contact-title">
          Say hello.<br />
          <span className="italic text-coral-700">Really.</span>
        </h1>
        <p className="mt-5 text-ink/75 max-w-2xl leading-relaxed">
          Wingwomen is built by a small team who reads every note. Whether you have a story
          to share, a question to ask, or a bug to report, we want to hear from you.
        </p>

        {/* Primary card */}
        <div className="mt-10 grid md:grid-cols-12 gap-6">
          <div className="md:col-span-7 bg-white border border-border rounded-2xl p-8 shadow-card">
            <div className="text-xs tracking-[0.2em] uppercase text-sage-600 mb-3 flex items-center gap-1.5">
              <EnvelopeSimple size={14} weight="duotone" /> Email us
            </div>
            <a href={`mailto:${EMAIL}?subject=${encodeURIComponent("Hi Wingwomen")}`}
               data-testid="contact-mailto"
               className="font-display text-xl sm:text-2xl leading-tight text-coral-700 hover:text-coral-600 underline decoration-coral/40 hover:decoration-coral underline-offset-8 transition-colors inline-block">
              {EMAIL}
            </a>
            <div className="mt-6 flex flex-wrap items-center gap-3">
              <a href={`mailto:${EMAIL}?subject=${encodeURIComponent("Hi Wingwomen")}`}
                 data-testid="contact-open-mail"
                 className="inline-flex items-center gap-2 bg-coral text-white px-5 py-2.5 rounded-full text-sm font-medium hover:bg-coral-600 transition-colors">
                <EnvelopeSimple size={14} weight="duotone" /> Open in mail
              </a>
              <button onClick={copy} data-testid="contact-copy-email"
                      className="inline-flex items-center gap-2 border border-border px-5 py-2.5 rounded-full text-sm hover:border-ink transition-colors">
                {copied ? <><Check size={14} weight="bold" />Copied</> : <><Copy size={14} />Copy email</>}
              </button>
            </div>
            <p className="mt-6 text-xs text-muted-foreground leading-relaxed">
              We usually reply within one business day. For medical emergencies, please call your
              local emergency number, this inbox is not monitored 24/7.
            </p>
          </div>

          <div className="md:col-span-5 space-y-3">
            {REASONS.map(({ icon: Icon, title, body }) => (
              <div key={title} className="bg-white border border-border rounded-2xl p-5">
                <Icon size={22} weight="duotone" className="text-sage" />
                <div className="font-display text-lg mt-2">{title}</div>
                <p className="text-sm text-ink/70 mt-1 leading-relaxed">{body}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Not-a-form note */}
        <div className="mt-10 p-6 bg-sage-50 border border-sage-100 rounded-2xl text-sm text-ink/80 leading-relaxed">
          <strong>Please don't share personal health details here.</strong> Emails aren't a
          HIPAA-compliant channel. If you'd like to discuss anything about your care, please
          save it for a licensed clinician. We're here for community, product, and privacy questions.
        </div>
      </main>

      <SiteFooter compact />
    </div>
  );
}
